import { memo, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
// Local files
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useManagers from 'hooks/useManagers';
import useApp from 'hooks/useApp';
import useSuccess from 'hooks/useSuccess';
import { DELETE_MANAGER } from 'helpers/confirmations';
import { checkValidEmail } from 'helpers';
import { managersSelectors } from 'features/managers';
import { appSelectors } from 'features/app'

const ManagersDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const { openDeleteDialog, setManagerDrawer } = useApp();
  const { updateManager } = useManagers();
  const { setSuccess } = useSuccess();
  const fetchedManager = useCustomSelector(managersSelectors.getManagerData);
  const { open, edit } = useCustomSelector(appSelectors.getManagerDrawerData);
  const fetching = useCustomSelector(managersSelectors.managerIsFetching);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState({ value: '', error: '' });

  const handleClose = () => setManagerDrawer({ open: false, edit: false });
  const handleEntering = () => { 
    setFirstName(fetchedManager?.first_name || '');
    setLastName(fetchedManager?.last_name || '');
    setEmail({ value: fetchedManager?.email || '', error: '' });
  };
  const handleExited = () => { 
    setFirstName('');
    setLastName('');
    setEmail('');
  };
  const needToUpdate = (a,b) => a.first_name !== b.first_name || a.last_name !== b.last_name || a.email !== b.email;
  const handleSave = () => {
    let hasError = false;

    if (!checkValidEmail(email.value)) {
      setEmail(prev => ({ ...prev, error: `Email is invalid` }) );
      hasError = true;
    }
    if (hasError) return;

    const manager = { id: fetchedManager.id, first_name: firstName || null, last_name: lastName || null, email: email.value };

    if (needToUpdate(fetchedManager, manager)) {
      updateManager(manager)
      .then(({ error }) => {
        if (!error) {
          setSuccess('Manager successfully updated');
          handleClose();
        }
      })
    } else {  
      handleClose();
    }
  };
  const handleDelete = () => {
    handleClose();
    openDeleteDialog({ id: fetchedManager.id, type: DELETE_MANAGER });
  };

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      onClose={handleClose}
      SlideProps={{ onExited: handleExited, onEntering: handleEntering }}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
          <Button color='error' disabled={fetching} onClick={handleDelete} sx={{ fontWeight: 700 }}>Delete</Button>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
          {!edit ? 'Create' : 'Edit'} User
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
        <TextField
          fullWidth
          margin='normal'
          label='First name'
          disabled={fetching}
          value={firstName}
          onChange={({ target: { value } }) => setFirstName(value)}
        />
        <TextField
          fullWidth
          margin='normal'
          label='Last name'
          disabled={fetching}
          value={lastName}
          onChange={({ target: { value } }) => setLastName(value)}
        />
        <TextField
          fullWidth
          margin='normal'
          label='Email'
          disabled={fetching}
          value={email.value}
          onChange={({ target: { value } }) => setEmail({ value, error: '' })}
          helperText={email.error}
          error={!!email.error}
        />
        <Button
          variant='contained'
          sx={{ display: 'flex', mx: 'auto', px: 5, mt: 3 }}
          disabled={fetching}
          onClick={handleSave}
        >
          Save
        </Button>
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <ManagersDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);